import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import AppShell from "../components/AppShell";
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import { api, useAuth } from "../context/AuthContext";
import { Newspaper, ArrowRight, Loader2 } from "lucide-react";

export default function News() {
  const { user, loading } = useAuth();
  const [articles, setArticles] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinel = useRef(null);
  const PAGE = 18;

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const offset = (articles || []).length;
      // offset=0 overlaps the initial fetch; dedupe by id below
      const r = await api.get(`/news?limit=${PAGE}&offset=${offset === 0 ? 0 : offset}`);
      const rows = r.data.articles || [];
      setArticles((prev) => {
        const seen = new Set((prev || []).map((a) => a.id));
        return [...(prev || []), ...rows.filter((a) => !seen.has(a.id))];
      });
      if (rows.length < PAGE) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [articles]);

  useEffect(() => {
    api.get(`/news?limit=${PAGE}&offset=0`).then((r) => {
      setArticles(r.data.articles || []);
      if ((r.data.articles || []).length < PAGE) setHasMore(false);
    }).catch(() => { setArticles([]); setHasMore(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasMore || articles === null) return;
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) loadMore();
      },
      { rootMargin: "600px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, articles, loadingMore, loadMore]);

  if (loading && !articles) return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;

  const body = (
    <>
      <SEO
        title="Journal — slots, math and volatility"
        description="Honest, math-first articles on slots, bankroll, RTP and volatility from Gumble Studio."
        path="/news"
      />
      <div className={`${user ? "pt-10 md:pt-14" : "pt-28"} px-6 md:px-10 max-w-6xl mx-auto pb-20`}>
        <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] text-white/40">
          <Newspaper className="w-3.5 h-3.5" /> Editorial
        </div>
        <h1 className="mt-2 text-4xl md:text-6xl font-black tracking-[-0.03em]">The Gumble Journal</h1>
        <p className="mt-4 max-w-2xl text-white/60 text-[15px] leading-relaxed">
          Long-form pieces on slots, math, volatility, and how our studio builds its games. No fluff, no filler — read, then spin.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {!articles && (
            <div className="col-span-2 flex justify-center py-20 text-white/40"><Loader2 className="animate-spin" /></div>
          )}
          {articles && articles.length === 0 && (
            <div className="col-span-2 text-white/50">No articles yet.</div>
          )}
          {articles && articles.map((a) => (
            <Link key={a.id} to={`/news/${a.slug}`} data-testid={`news-card-${a.slug}`}
              className="group glass rounded-3xl overflow-hidden hover:glass-strong transition-all">
              {a.cover && (
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={a.cover} alt={`${a.title} — article cover`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-6">
                <div className="text-[11px] uppercase tracking-widest text-white/40">{new Date(a.created_at).toLocaleDateString()} · {a.author || "Gumble Editorial"}</div>
                <div className="mt-2 text-[19px] font-semibold tracking-tight leading-snug">{a.title}</div>
                {a.excerpt && <p className="mt-3 text-white/60 text-[14px] leading-relaxed line-clamp-3">{a.excerpt}</p>}
                <div className="mt-4 inline-flex items-center gap-1.5 text-white text-[13px] font-medium">
                  Read article <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div ref={sentinel} aria-hidden className="h-2" />
        {loadingMore && (
          <div className="flex justify-center py-10 text-white/40"><Loader2 className="animate-spin" /></div>
        )}
        {articles && articles.length > 0 && !hasMore && (
          <div className="text-center py-10 text-[13px] text-white/35">
            You&apos;ve read them all — {articles.length} articles.
          </div>
        )}
        <Archive />
      </div>
      <Footer />
    </>
  );

  if (user) return <AppShell>{body}</AppShell>;
  return (
    <div className="relative min-h-screen text-white" data-testid="news-page">
      <TopNav variant="app" />
      {body}
    </div>
  );
}

// Full archive: every article linked on one page (sitemap discovery backup).
function Archive() {
  const [all, setAll] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || all) return;
    api.get("/news?limit=500&offset=0").then((r) => setAll(r.data.articles || [])).catch(() => setAll([]));
  }, [open, all]);
  return (
    <div className="mt-14 glass rounded-3xl p-6 md:p-8">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left">
        <span className="text-[15px] font-semibold">Browse the full archive</span>
        <span className="text-white/40 text-[13px]">{open ? "Hide" : "Show all"}</span>
      </button>
      {open && (
        <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
          {(all || []).map((a) => (
            <li key={a.id}>
              <Link to={`/news/${a.slug}`} className="text-[13.5px] text-white/65 hover:text-white leading-relaxed">
                {a.title}
              </Link>
            </li>
          ))}
          {all === null && <li className="text-white/40 text-[13px]">Loading…</li>}
        </ul>
      )}
    </div>
  );
}
